import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from '@emotion/styled'
import { Link } from 'react-router-dom'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { usePortalAuth } from '../hooks/usePortalAuth'
import { usePortalDisplayName } from '../hooks/usePortalDisplayName'
import useAppState from '../hooks/useAppState'
import { getGrooverText, formatGrooverText } from '../libs/GrooverTextRepository'
import { usePrivateLessonsForUser } from '../hooks/usePrivateLessonsForUser'
import { useDropinTicketsForUser } from '../hooks/useDropinTicketsForUser'
import { useEnrollmentsForPortalUser } from '../hooks/useEnrollmentsForPortalUser'
import { getPortalCourseEnrollments } from './portal-enrollments/portalEnrollmentHelpers'
import { usePortalQueryClientId } from '../hooks/portalHostQueryGate'
import { DropinTicketStatus } from '../../../pure-js/types/GrooverTypes'
import {
  flushPortalLoginPerfDashboardMilestone,
  markPortalLoginPerf,
  measurePortalLoginPerfSpan
} from '../libs/PortalLoginPerfHelper'

const PageContainer = styled.div`
  background: #ffffff;
  color: #1f2937;
  width: 100%;
  min-width: 0;
`

const MainContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 32px 40px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    max-width: 1400px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 16px 12px;
    min-width: 0;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 28px 0;
  line-height: 1.5;
`

const StatList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 420px;
`

const StatRowLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  text-decoration: none;
  color: inherit;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    box-shadow: 0 2px 10px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.12);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.35);
  }
`

const StatLabel = styled.span`
  font-weight: 500;
  color: #374151;
`

const StatValueRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

const StatValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: var(--portal-primary, #7c3aed);
`

const StatChevron = styled(ChevronRightIcon)`
  font-size: 22px !important;
  color: #9ca3af;
`

function firstName(displayName: string | undefined): string {
  if (!displayName?.trim()) return ''
  return displayName.trim().split(/\s+/)[0] ?? displayName
}

export const PortalDashboardPage: React.FC = () => {
  const { portalUser, portalClaimsReady } = usePortalAuth()
  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const clientId = usePortalQueryClientId()
  const { displayName } = usePortalDisplayName(enrollmentUserId)
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage
  const getText = useCallback((key: string) => getGrooverText(key, selectedLanguage), [selectedLanguage])
  const dashboardPerfFlushedRef = useRef(false)

  useEffect(() => {
    markPortalLoginPerf('dashboardMounted')
  }, [])

  const {
    data: lessons,
    isLoading: lessonsLoading,
    error: lessonsError
  } = usePrivateLessonsForUser(enrollmentUserId, clientId)
  const {
    data: dropinTickets,
    isLoading: dropinLoading,
    error: dropinError
  } = useDropinTicketsForUser(enrollmentUserId, clientId)
  const {
    data: enrollments,
    isLoading: enrollmentsLoading,
    error: enrollmentsError
  } = useEnrollmentsForPortalUser(enrollmentUserId, clientId)

  const privateLessonsCount = lessons?.length ?? 0
  const enrollmentsCount = useMemo(() => getPortalCourseEnrollments(enrollments).length, [enrollments])

  const dropinVisibleCount = useMemo(() => {
    const list = dropinTickets ?? []
    return list.filter((t) => t.status !== DropinTicketStatus.PendingPayment && t.status !== DropinTicketStatus.Deleted)
      .length
  }, [dropinTickets])

  const loading = !portalClaimsReady || lessonsLoading || dropinLoading || enrollmentsLoading
  const error = lessonsError || dropinError || enrollmentsError

  useEffect(() => {
    if (dashboardPerfFlushedRef.current || loading || !portalUser) {
      return
    }
    dashboardPerfFlushedRef.current = true
    measurePortalLoginPerfSpan('dashboardDataReadyMs', 'dashboardMounted')
    void flushPortalLoginPerfDashboardMilestone()
  }, [loading, portalUser])

  const welcomeName = firstName(displayName)

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>{getText('portalDashboardLoading')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <MainContent>
          <PageTitle>{getText('portalWelcomeTitleGeneric')}</PageTitle>
          <Subtitle>{getText('portalDashboardError')}</Subtitle>
        </MainContent>
      </PageContainer>
    )
  }

  const rows: { key: string; label: string; count: number; to: string }[] = []
  if (enrollmentsCount > 0) {
    rows.push({
      key: 'enr',
      label: getText('portalHomeStatEnrollments'),
      count: enrollmentsCount,
      to: buildRoute(RoutePath.PORTAL_ENROLLMENTS)
    })
  }
  if (privateLessonsCount > 0) {
    rows.push({
      key: 'pl',
      label: getText('portalHomeStatPrivateLessons'),
      count: privateLessonsCount,
      to: buildRoute(RoutePath.PORTAL_HOME_ORDERS)
    })
  }
  if (dropinVisibleCount > 0) {
    rows.push({
      key: 'di',
      label: getText('portalHomeStatDropinTickets'),
      count: dropinVisibleCount,
      to: buildRoute(RoutePath.PORTAL_DROPIN_TICKETS)
    })
  }

  return (
    <PageContainer>
      <MainContent>
        <PageTitle>
          {welcomeName
            ? formatGrooverText(getText('portalWelcomeTitle'), [welcomeName])
            : getText('portalWelcomeTitleGeneric')}
        </PageTitle>
        {rows.length > 0 && <Subtitle>{getText('portalHomeSubtitle')}</Subtitle>}
        {rows.length > 0 && (
          <StatList>
            {rows.map((r) => (
              <li key={r.key} style={{ margin: 0, padding: 0 }}>
                <StatRowLink
                  to={r.to}
                  aria-label={formatGrooverText(getText('portalDashboardStatLinkAria'), [r.label])}
                >
                  <StatLabel>{r.label}</StatLabel>
                  <StatValueRow>
                    <StatValue>{r.count}</StatValue>
                    <StatChevron aria-hidden />
                  </StatValueRow>
                </StatRowLink>
              </li>
            ))}
          </StatList>
        )}
      </MainContent>
    </PageContainer>
  )
}
